/* eslint-disable max-lines */
/* eslint-disable max-len */
/* eslint-disable react/no-unstable-nested-components */
import { GraphLegend, NonIdealState } from '@acp/core-ui';
import {
    Box,
    Image,
    VStack, useBreakpointValue, useToken,
} from '@chakra-ui/react';
import {
    BarCustomLayerProps, BarDatum, ComputedDatum, ResponsiveBar,
} from '@nivo/bar';
import { BasicTooltip } from '@nivo/tooltip';
import {
    PortfolioFacets, ReportedFindingsFacets, StatsData, StateShortName,
} from '@acp/shared';
import { useNavigate } from 'react-router-dom';
import { useMemo } from 'react';
import { brandIcons } from '@acp/core-ui/assets';
import { useExperiment, useSession } from '@acp/feature/auth';
import { ActionRequiredTab, useActionRequiredTab } from '../../hooks/use-action-required-tab';
import { useGetStats } from '../../hooks/use-get-stats';
import { usePropertyManager } from '../../../agency';
import { BarChartSkeleton } from './graph-skeleton';

// Lets us customise the display value of bar totals,
// by reimplementing bar totals instead of using Nivo's built-in one.
// Adapted from GH issue:
// https://github.com/plouc/nivo/issues/2020#issuecomment-1140295641
function BarTotalsLayer({
    bars,
    yScale,
    xScale,
}: BarCustomLayerProps<BarDatum>) {
    const labelOffset = 10;
    const labelFontSize = 12;
    if (bars.length === 0) return null;

    // compute totals for each index/bar
    const totals: Record<string | number, {
        actualTotal: number;
        value: number;
    }> = {};

    const bandwidth = bars[0].width;

    bars.forEach((bar) => {
        const { indexValue } = bar.data;

        if (!(indexValue in totals)) {
            totals[indexValue] = {
                actualTotal: 0,
                value: 0,
            };
        }

        // Can use totals[].actualValue to tally the actual total for each bar
        // and use totals[].value to know how high to place the label
        if (!bar.data.hidden && bar.data.value !== null && bar.data.data[`${bar.data.id}Actual`] !== undefined) {
            totals[indexValue].actualTotal += bar.data.data[`${bar.data.id}Actual`] as number;
            totals[indexValue].value += bar.data.value!;
        }
    });

    // place text elements above the bars
    const labels = Object.keys(totals).map((indexValue) => {
        const x = xScale(indexValue as unknown as number) + bandwidth / 2;
        const y = yScale(totals[indexValue].value) - labelOffset;

        return (
            <text
                key={`total.${indexValue}`}
                x={x}
                y={y}
                textAnchor="middle"
                fontWeight="bold"
                fontSize={labelFontSize}
            >
                {totals[indexValue].actualTotal}
            </text>
        );
    });

    return labels;
}

type BuildReportedFindingsParams = {
    isVic: boolean;
    urgentReportedFindings: number;
    nonUrgentReportedFindings: number;
    urgentColor: string;
    nonUrgentColor: string;
    propertyManagerId: string | null | undefined;
    reportedFindingsTotal: number;
};

function buildReportedFindingsBarDatum({
    isVic,
    urgentReportedFindings,
    nonUrgentReportedFindings,
    urgentColor,
    nonUrgentColor,
    propertyManagerId,
    reportedFindingsTotal,
}: BuildReportedFindingsParams) {
    if (isVic) {
        return {
            labelName: 'Reported findings',
            type: 'Reported findings',
            UrgentActual: urgentReportedFindings,
            Urgent: 100,
            UrgentColor: urgentColor,
            UrgentTab: ActionRequiredTab.ReportedFindings,
        };
    }

    return {
        labelName: 'Reported findings',
        type: 'Reported findings',
        UrgentActual: urgentReportedFindings,
        Urgent: (100 / reportedFindingsTotal) * urgentReportedFindings,
        UrgentColor: urgentColor,
        UrgentTab: ActionRequiredTab.ReportedFindings,
        'Non urgentActual': nonUrgentReportedFindings,
        'Non urgent': (100 / reportedFindingsTotal) * nonUrgentReportedFindings,
        'Non urgentColor': nonUrgentColor,
        'Non urgentFacet': [ReportedFindingsFacets.Compliant, ReportedFindingsFacets.PendingItems],
        'Non urgentPropertyManager': propertyManagerId,
    };
}

function useGraphData({
    data,
    propertyManagerId,
}: {
    data: StatsData['actionRequired'];
    propertyManagerId?: string | null;
}) {
    const { complianceAgencyState } = useSession();
    const experiments = useExperiment();
    const urgentBarColor = useToken('colors', 'danger.400');
    const nonUrgentBarColor = useToken('colors', 'danger.200');
    const newManagementsBarColor = useToken('colors', 'primary.300');

    const { URGENT_NOTICES, NON_URGENT_NOTICES } = data.ACTIVE_NOTICES_STATS;
    const { OUTSTANDING_INVOICES, OVERDUE_INVOICES } = data.INVOICES_STATS;
    const { NEW_MANAGEMENT } = data.NEW_MANAGEMENT_STATS;
    const { URGENT_REPORTED_FINDINGS, NON_URGENT_REPORTED_FINDINGS } = data.REPORTED_FINDINGS_STATS;
    const isVic = complianceAgencyState.toLocaleLowerCase() === StateShortName.VIC;

    const graphData = useMemo(() => {
        const reportedFindingsTotal = isVic
            ? URGENT_REPORTED_FINDINGS
            : (URGENT_REPORTED_FINDINGS + NON_URGENT_REPORTED_FINDINGS);
        const activeNoticeTotal = (URGENT_NOTICES + NON_URGENT_NOTICES);
        const invoiceTotal = (OUTSTANDING_INVOICES + OVERDUE_INVOICES);

        const barDatum: unknown[] = [];

        if (activeNoticeTotal > 0) {
            barDatum.push({
                labelName: 'Access issues',
                type: 'Access issues',
                UrgentActual: URGENT_NOTICES,
                Urgent: activeNoticeTotal > 0 ? (100 / activeNoticeTotal) * URGENT_NOTICES : 0,
                UrgentColor: urgentBarColor,
                UrgentTab: ActionRequiredTab.UrgentActiveNotices,
                'Non urgentActual': NON_URGENT_NOTICES,
                'Non urgent': activeNoticeTotal > 0 ? (100 / activeNoticeTotal) * NON_URGENT_NOTICES : 0,
                'Non urgentColor': nonUrgentBarColor,
                'Non urgentFacet': [
                    (experiments.userExperiments?.AgencyEntryMethod ?? false)
                        ? PortfolioFacets.AccessIssues
                        : PortfolioFacets.ActiveNotices,
                ],
                'Non urgentPropertyManager': propertyManagerId,
            });
        }

        if (reportedFindingsTotal > 0) {
            const reportedFindingsDatum = buildReportedFindingsBarDatum({
                isVic,
                urgentReportedFindings: URGENT_REPORTED_FINDINGS,
                nonUrgentReportedFindings: NON_URGENT_REPORTED_FINDINGS,
                urgentColor: urgentBarColor,
                nonUrgentColor: nonUrgentBarColor,
                propertyManagerId,
                reportedFindingsTotal,
            });

            barDatum.push(reportedFindingsDatum);
        }

        if (invoiceTotal > 0) {
            barDatum.push({
                labelName: 'Invoices',
                type: 'Invoices',
                UrgentActual: OVERDUE_INVOICES,
                Urgent: invoiceTotal > 0 ? (100 / invoiceTotal) * OVERDUE_INVOICES : 0,
                UrgentColor: urgentBarColor,
                UrgentTab: ActionRequiredTab.OverdueInvoices,
                'Non urgentActual': OUTSTANDING_INVOICES,
                'Non urgent': invoiceTotal > 0 ? (100 / invoiceTotal) * OUTSTANDING_INVOICES : 0,
                'Non urgentColor': nonUrgentBarColor,
                'Non urgentFacet': [PortfolioFacets.OutstandingInvoices, PortfolioFacets.OverdueInvoices],
                'Non urgentPropertyManager': propertyManagerId,
            });
        }

        if (NEW_MANAGEMENT > 0) {
            barDatum.push({
                labelName: 'New managements',
                type: 'New managements',
                'New managementsActual': NEW_MANAGEMENT,
                'New managements': NEW_MANAGEMENT > 0 ? 100 : 0,
                'New managementsColor': newManagementsBarColor,
                'New managementsTab': ActionRequiredTab.NewManagements,
            });
        }

        return barDatum;
    }, [
        URGENT_NOTICES,
        NON_URGENT_NOTICES,
        OUTSTANDING_INVOICES,
        OVERDUE_INVOICES,
        NEW_MANAGEMENT,
        URGENT_REPORTED_FINDINGS,
        NON_URGENT_REPORTED_FINDINGS,
        propertyManagerId,
        experiments.userExperiments?.AgencyEntryMethod,
        isVic,
    ]);

    const keys = useMemo(() => {
        const barKeys = [
            'Urgent',
            'Non urgent',
        ];

        if (NEW_MANAGEMENT > 0) {
            barKeys.push('New managements');
        }

        return barKeys;
    }, [
        NEW_MANAGEMENT,
    ]);

    return [graphData, keys] as const;
}

export function ActionsRequiredGraph({ propertyManagerId }: { propertyManagerId?: string | null }) {
    const {
        setTabByName,
    } = useActionRequiredTab();

    const newManagementsBarColor = useToken('colors', 'primary.300');
    const fontSizeSm = useToken('fontSizes', 'sm');
    const fontSizeXs = useToken('fontSizes', 'xs');

    const barMargin = useBreakpointValue({
        base: {
            top: 50, right: 30, bottom: 70, left: 0,
        },
        sm: {
            top: 50, right: 30, bottom: 60, left: 40,
        },
    });

    const navigate = useNavigate();

    const { isLoading, data } = useGetStats(propertyManagerId);
    const {
        propertyManager: selectedPropertyManager,
    } = usePropertyManager(propertyManagerId ?? null);

    const [graphData, keys] = useGraphData({
        data: data?.actionRequired ?? {
            ACTIVE_NOTICES_STATS: {
                URGENT_NOTICES: 0,
                NON_URGENT_NOTICES: 0,
            },
            COMPLIANCE_QUOTES_STATS: {
                URGENT_QUOTATIONS: 0,
                NON_URGENT_QUOTATIONS: 0,
            },
            INVOICES_STATS: {
                OUTSTANDING_INVOICES: 0,
                OVERDUE_INVOICES: 0,
            },
            NEW_MANAGEMENT_STATS: {
                NEW_MANAGEMENT: 0,
            },
            REPORTED_FINDINGS_STATS: {
                URGENT_REPORTED_FINDINGS: 0,
                NON_URGENT_REPORTED_FINDINGS: 0,
            },
        },
        propertyManagerId,
    });

    if (isLoading || data === undefined) {
        return <BarChartSkeleton />;
    }

    const handleClickBar = (datum: ComputedDatum<BarDatum>) => {
        const facet = datum.data[`${datum.id}Facet`] as unknown as PortfolioFacets[] | undefined;
        const propertyManager = datum.data[`${datum.id}PropertyManager`] as unknown as string | null | undefined;
        const tab = datum.data[`${datum.id}Tab`];
        const datumType = datum.data.type;

        if (facet !== undefined) {
            const searchParams = new URLSearchParams();

            facet.forEach((f) => {
                searchParams.append('facet', f);
            });

            if (propertyManager !== undefined && propertyManager !== null) {
                searchParams.append('property-manager', propertyManager);
            }

            if (datumType === 'Reported findings') {
                navigate(`/action-required/reported-findings?${searchParams.toString()}`, {
                    replace: true,
                });

                return;
            }

            navigate(`/portfolio?${searchParams.toString()}`, {
                replace: true,
            });
        }

        if (tab !== undefined) {
            // Not great, works perfectly
            // There's no chance that we'll be able to pass the ref of the tab down here
            document.getElementById('action-required')?.scrollIntoView();
            setTabByName(tab as ActionRequiredTab);
        }
    };

    if (graphData.length === 0) {
        return (
            <NonIdealState
                iconComponent={<Image src={brandIcons.noActiveNotice} />}
                title={`No immediate actions required ${selectedPropertyManager?.name !== undefined ? ` for ${selectedPropertyManager.name}` : ''}`}
                description="You are all up to date!"
                py={24}
            />
        );
    }

    const getLegendData = (
        labels: string[],
        barData: BarDatum[]
    ) => labels.map((label) => {
        const datum = barData.find((bd) => bd[`${label}Color`] !== undefined);
        if (datum) {
            return {
                label,
                legendIconColor: datum[`${label}Color`] as string,
            };
        }
        return {
            label,
            legendIconColor: null,
        };
    });

    return (
        <VStack w="full">
            <Box w="99%" h={96} className="svg-overflow-visible">
                <ResponsiveBar
                    data={graphData as unknown as BarDatum[]}
                    keys={keys}
                    indexBy="type"
                    margin={barMargin}
                    padding={0.3}
                    valueScale={{ type: 'linear' }}
                    indexScale={{ type: 'band', round: true }}
                    axisLeft={null}
                    colors={({ data: colorData, id }) => {
                        if (id === 'New managements') {
                            return newManagementsBarColor;
                        }
                        return colorData[`${id}Color`] as string;
                    }}
                    tooltip={({
                        color, formattedValue, indexValue, id, data: tooltipData,
                    }) => (
                        <BasicTooltip
                            id={id === indexValue ? tooltipData.labelName : `${tooltipData.labelName} (${id})`}
                            value={tooltipData[`${id}Actual`] !== undefined ? tooltipData[`${id}Actual`] : formattedValue}
                            enableChip
                            color={color}
                        />
                    )}
                    enableGridY={false}
                    labelSkipWidth={12}
                    labelSkipHeight={25}
                    labelTextColor="black"
                    layers={[
                        'grid',
                        'axes',
                        'markers',
                        'bars',
                        'legends',
                        'annotations',
                        BarTotalsLayer,
                    ]}
                    enableLabel
                    label={(d) => `${d.data[`${d.id}Actual`]}`}
                    theme={{
                        text: {
                            fontFamily: "'Roboto'",
                        },
                        labels: {
                            text: {
                                fontSize: fontSizeSm,
                            },
                        },
                        tooltip: {
                            container: {
                                fontSize: fontSizeSm,
                            },
                            chip: {
                                borderRadius: '100px',
                            },
                        },
                        legends: {
                            text: {
                                fontFamily: "'Roboto'",
                                fontSize: fontSizeXs,
                            },
                        },
                        axis: {
                            ticks: {
                                text: {
                                    color: 'blue',
                                },
                            },
                            legend: {
                                text: {
                                    color: 'blue',
                                },
                            },
                        },
                    }}
                    onClick={handleClickBar}
                    onMouseEnter={(_datum, event) => {
                        // Not great, works perfectly
                        // https://github.com/plouc/nivo/issues/2276#issuecomment-1496478882
                        // eslint-disable-next-line no-param-reassign
                        event.currentTarget.style.cursor = 'pointer';
                    }}
                />
            </Box>
            <GraphLegend data={getLegendData(keys, graphData as BarDatum[])} />
        </VStack>
    );
}
